import { create } from 'zustand';
import type { Unsubscribe } from '@firebase/firestore';
import { ChecklistThreadMessage } from '../types';
import { checklistThreadsApi, PostChecklistMessageData } from '../api/checklistThreads';

/**
 * Cache of checklist-item threads, keyed by `${structureId}:${itemId}`. Each
 * entry holds its live-subscription unsubscribe so components can open/close
 * discussions without leaking listeners.
 */
interface ThreadEntry {
  messages: ChecklistThreadMessage[];
  isLoading: boolean;
  unsubscribe?: Unsubscribe;
}

interface ChecklistThreadsState {
  threads: Record<string, ThreadEntry>;
  isPosting: boolean;

  getThread: (structureId: string, itemId: string) => ThreadEntry | undefined;
  subscribe: (structureId: string, itemId: string) => void;
  unsubscribe: (structureId: string, itemId: string) => void;
  unsubscribeAll: () => void;
  postMessage: (
    structureId: string,
    itemId: string,
    data: PostChecklistMessageData
  ) => Promise<void>;
  /**
   * Update the text of an existing thread message. The live subscription
   * delivers the change; no manual state patch needed. Rethrows on
   * failure so the caller can restore draft text and show an error.
   */
  updateMessage: (
    structureId: string,
    itemId: string,
    messageId: string,
    text: string
  ) => Promise<void>;
  /**
   * Delete a thread message. The live subscription removes the message from
   * state. Rethrows on failure so the caller can surface an error.
   */
  deleteMessage: (structureId: string, itemId: string, messageId: string) => Promise<void>;
}

export const threadKey = (structureId: string, itemId: string) => `${structureId}:${itemId}`;

export const useChecklistThreadsStore = create<ChecklistThreadsState>((set, get) => ({
  threads: {},
  isPosting: false,

  getThread: (structureId, itemId) => get().threads[threadKey(structureId, itemId)],

  subscribe: (structureId, itemId) => {
    const key = threadKey(structureId, itemId);
    // Idempotent: skip if we already have an active listener for this thread.
    if (get().threads[key]?.unsubscribe) return;

    set((state) => ({
      threads: {
        ...state.threads,
        [key]: { messages: state.threads[key]?.messages ?? [], isLoading: true },
      },
    }));

    const unsub = checklistThreadsApi.subscribeMessages(
      structureId,
      itemId,
      (messages) => {
        set((state) => ({
          threads: {
            ...state.threads,
            [key]: {
              messages,
              isLoading: false,
              unsubscribe: state.threads[key]?.unsubscribe,
            },
          },
        }));
      },
      () => {
        set((state) => ({
          threads: {
            ...state.threads,
            [key]: { ...(state.threads[key] ?? { messages: [] }), isLoading: false },
          },
        }));
      }
    );

    set((state) => ({
      threads: {
        ...state.threads,
        [key]: { ...(state.threads[key] ?? { messages: [], isLoading: true }), unsubscribe: unsub },
      },
    }));
  },

  unsubscribe: (structureId, itemId) => {
    const key = threadKey(structureId, itemId);
    const entry = get().threads[key];
    entry?.unsubscribe?.();
    if (entry) {
      set((state) => ({
        threads: { ...state.threads, [key]: { ...entry, unsubscribe: undefined } },
      }));
    }
  },

  unsubscribeAll: () => {
    Object.values(get().threads).forEach((t) => t.unsubscribe?.());
    set({ threads: {} });
  },

  postMessage: async (structureId, itemId, data) => {
    set({ isPosting: true });
    try {
      await checklistThreadsApi.postMessage(structureId, itemId, data);
      // The active subscription will deliver the new message; no manual merge.
    } finally {
      set({ isPosting: false });
    }
  },

  updateMessage: async (structureId, itemId, messageId, text) => {
    await checklistThreadsApi.updateMessage(structureId, itemId, messageId, text);
    // The active subscription delivers the updated message; no manual patch.
  },

  deleteMessage: async (structureId, itemId, messageId) => {
    await checklistThreadsApi.deleteMessage(structureId, itemId, messageId);
    // The active subscription removes the message from state; no manual patch.
  },
}));
